import { AvatarAccent, InitialsAvatar, Text } from "@growtherapy/sprout-ui";
import type { VideoPlayer } from "@zoom/videosdk";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import { RefObject } from "react";
import { useMatch } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { ActiveSpeakerOutline, NameBadge } from "../../components";
import { PROVIDER_PATH } from "../../config";
import { nonEssentialElementProps, useIsZoomEnabled } from "../../hooks";
import { UserType } from "../../types";
import { nameToInitials } from "../../utils";
import {
  activelySpeakingParticipantIdsAtom,
  isOwnAudioOffAtom,
  isOwnVideoOffAtom,
  isScreenShareModeAtom,
  parseIdentity,
  useRemoteVideoComponent,
} from "../../video-sdk";
import {
  remoteMediaStatusByParticipantIdAtom,
  userDataFamily,
} from "../../video-sdk/twilio";
import { isWhiteboardModeAtom } from "../messages/state";
import { UseRemoteMediaProps, useRemoteMedia } from "../useRemoteMedia";
import { useTriggerPictureInPicture } from "./useTriggerPictureInPicture";

export type NameBadgeWrapperProps = {
  className?: string;
  participantIdentity: string;
};

function NameBadgeWrapper({
  className,
  participantIdentity,
}: NameBadgeWrapperProps) {
  const { name: participantName } = parseIdentity(participantIdentity ?? "");
  const audioStatus = useAtomValue(remoteMediaStatusByParticipantIdAtom)[
    participantIdentity ?? ""
  ];
  const userData = useAtomValue(userDataFamily(participantIdentity));

  return (
    <NameBadge
      audioMuted={audioStatus?.isAudioMuted ?? true}
      name={participantName}
      pronouns={userData?.pronouns}
      className={className}
      isSelf={false}
    />
  );
}

export type RemoteParticipantProps = {
  className?: string;
  isProviderView?: boolean;
  isVideoOff?: boolean;
  isOwnAudioOff?: boolean;
  isOwnVideoOff?: boolean;
  isScreenShareMode?: boolean;
  isWhiteboardMode?: boolean;
  isSpeaking?: boolean;
  videoRef: RefObject<HTMLVideoElement | VideoPlayer>;
  audioRef: RefObject<HTMLAudioElement>;
  nameBadgeComponent: typeof NameBadgeWrapper;
} & UseRemoteMediaProps;

export type RemoteParticipantWrapperProps = Pick<
  RemoteParticipantProps,
  "className"
> &
  UseRemoteMediaProps;

function getVideoOffMessaging(
  subject: string,
  isOwnVideoOff?: boolean,
  isOwnAudioOff?: boolean,
) {
  const tokens = [];
  if (!isOwnVideoOff) tokens.push(" see");
  if (!isOwnAudioOff) tokens.push(" hear");
  const videoOffAction = tokens.join(" and");
  const sentenceEnding =
    isOwnVideoOff && isOwnAudioOff
      ? "."
      : ` but they can still ${videoOffAction} you.`;

  return `${subject} has their camera off${sentenceEnding}`;
}

export function RemoteParticipantCell({
  audioRef,
  className,
  isOwnAudioOff,
  isOwnVideoOff,
  isProviderView,
  isScreenShareMode,
  isWhiteboardMode,
  isSpeaking,
  isVideoOff,
  nameBadgeComponent: NameBadgeComponent,
  remoteParticipant,
  videoRef,
}: RemoteParticipantProps) {
  const RemoteVideo = useRemoteVideoComponent();
  const {
    name: participantName,
    id,
    userType,
  } = parseIdentity(remoteParticipant?.identity ?? "");

  const remoteParticipantIsProvider = userType === UserType.PROVIDER;
  const displayInitials = participantName
    ? nameToInitials(participantName)
    : "";

  let videoOffMessageSubject = remoteParticipantIsProvider
    ? "Your provider"
    : "This person";

  if (isProviderView) {
    videoOffMessageSubject = "Your client";
  }
  const showVideoOffMessaging = !isScreenShareMode || !isWhiteboardMode;
  const videoOffMessaging = getVideoOffMessaging(
    videoOffMessageSubject,
    isOwnVideoOff,
    isOwnAudioOff,
  );

  const [zoomIsEnabled] = useIsZoomEnabled();

  return (
    <div
      className={twMerge(
        "relative overflow-hidden bg-neutral-900 rounded-2xl flex justify-center items-center",
        className,
      )}
      data-testid={`remote-audio-video:${id}`}
      /* 
         For accessibility: The aria-label and aria-labelledby attributes are not well supported 
         on the HTML5 <audio> and <video> elements at this time. In the meantime, we can 
         add the label to the parent element.
        */
      role="region"
      aria-label="Live remote participant video and audio"
    >
      {isVideoOff && (
        <div className="absolute isolate flex flex-col items-center">
          <InitialsAvatar
            initials={displayInitials}
            size={isScreenShareMode || isWhiteboardMode ? "sm" : "md"}
            className={classNames(
              "text-center flex items-center justify-center",
            )}
            accent={AvatarAccent.Green}
          />
          {showVideoOffMessaging && (
            <Text
              variant={isScreenShareMode || isWhiteboardMode ? "xs" : "sm"}
              className="text-neutral-600 p-4 text-center"
              data-testid="video-off-messaging"
            >
              {videoOffMessaging}
            </Text>
          )}
        </div>
      )}

      <RemoteVideo
        remoteParticipantId={id}
        audioRef={audioRef}
        videoRef={videoRef}
      />
      {participantName && (
        <NameBadgeComponent
          participantIdentity={remoteParticipant.identity}
          className={classNames(
            `absolute bottom-0 left-0 ${nonEssentialElementProps.className}`,
            { "z-[1]": zoomIsEnabled },
          )}
        />
      )}
      <ActiveSpeakerOutline isOutlined={!!isSpeaking} />
    </div>
  );
}

export function RemoteParticipantWrapper({
  remoteParticipant,
  className,
  ...listeners
}: RemoteParticipantWrapperProps) {
  const { audioRef, videoRef } = useRemoteMedia({
    remoteParticipant,
    ...listeners,
  });
  const isOwnAudioOff = useAtomValue(isOwnAudioOffAtom);
  const isOwnVideoOff = useAtomValue(isOwnVideoOffAtom);
  const remoteMediaStatusByParticipantId = useAtomValue(
    remoteMediaStatusByParticipantIdAtom,
  );
  const videoDelegatorStatus =
    remoteMediaStatusByParticipantId[remoteParticipant.identity ?? ""];
  const isVideoOff = videoDelegatorStatus?.isVideoOff ?? true;
  const isScreenShareMode = useAtomValue(isScreenShareModeAtom);
  const isWhiteboardMode = useAtomValue(isWhiteboardModeAtom);

  const isSpeaking = useAtomValue(activelySpeakingParticipantIdsAtom).has(
    remoteParticipant.identity,
  );
  const isProviderView = !!useMatch(PROVIDER_PATH);

  useTriggerPictureInPicture(remoteParticipant?.identity ?? "", videoRef);

  return (
    <RemoteParticipantCell
      audioRef={audioRef}
      className={className}
      isOwnAudioOff={isOwnAudioOff}
      isOwnVideoOff={isOwnVideoOff}
      isProviderView={isProviderView}
      isScreenShareMode={isScreenShareMode}
      isWhiteboardMode={isWhiteboardMode.value}
      isSpeaking={isSpeaking}
      isVideoOff={isVideoOff}
      nameBadgeComponent={NameBadgeWrapper}
      remoteParticipant={remoteParticipant}
      videoRef={videoRef}
    />
  );
}
